import { useState } from 'react'
import { message } from 'antd'
import { useRequest } from 'ahooks'
import classNames from 'classnames'
import { Modal, TextArea, Button } from '@/components'
import KeywordsEditor from '@/pages/datastores/components/KeywordsEditor.tsx'
import { updateSplitContent } from '@/apis/datastore/api.ts'
import { MAX_SPLIT_SIZE } from '@/features/datastore/constant'
import type { DocumentType } from '@/apis/datastore/model'
import type { Paragraph } from '@/pages/datastores/types/paragraph'

export interface ParagraphEditorModalProps {
  file_id: number
  chunk_id: number
  open?: boolean
  onCancel?: () => void
  content: string
  keywords?: string[]
  matched_keywords?: string[]
  extra_info?: Paragraph['extra_info']
  onEditSuccess?: (keywords: string[], content: string) => void
  file_type?: DocumentType
  title?: string | React.ReactElement
}

export function ParagraphEditorModal(props: ParagraphEditorModalProps) {
  const {
    open,
    onCancel: onModalClose,
    title = '编辑段落',
    // onFinish,
    // paragraph,
    file_id,
    chunk_id,
    matched_keywords,
    onEditSuccess,
    extra_info,
    // callback,
  } = props
  const [content, setContent] = useState(props.content)
  const [keywords, setKeyWords] = useState(props.keywords ?? [])
  const { runAsync, loading } = useRequest(updateSplitContent, { manual: true })
  const [editable, setEditable] = useState(false)

  const onSubmit = async () => {
    if (!content.trim()) {
      message.warning('段落内容不能为空')
      return
    }
    if (content.length > MAX_SPLIT_SIZE) {
      message.warning(`段落内容不能超过${MAX_SPLIT_SIZE}个字符`)
      return
    }
    await runAsync({
      file_id,
      chunk_id,
      content,
      keywords,
      enable: true,
      extra_info,
    })
    message.success('修改成功')
    setEditable(false)
    onEditSuccess?.(keywords, content)
  }

  return (
    <Modal
      title={title}
      open={open}
      maskClosable={false}
      onCancel={onModalClose}
      footer={null}
      width={1020}
      styles={{
        body: {
          padding: '0px 0px',
          minHeight: '420px',
          height: '67vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '8px',
        },
      }}
    >
      <div className='pt-20 pb-24 flex flex-col relative h-full flex-1'>
        <div className='px-32'>
          <KeywordsEditor
            defaultValue={keywords}
            highlightKeywords={matched_keywords}
            onChange={setKeyWords}
            readOnly={!editable}
          />
        </div>
        <div className='relative'>
          <div className='flex-1 mt-24 !px-30'>
            <TextArea
              spellCheck={false}
              readOnly={!editable}
              value={content}
              style={{ maxHeight: '47vh', resize: 'none' }}
              variant='borderless'
              className={classNames(
                ' shadow-none! resize-none! px-12px py-8px overflow-auto',
                {
                  'bg-[rgba(98,105,153,0.06)]': editable,
                  'bg-transparent!': !editable,
                  'pb-40px': true,
                },
              )}
              autoSize
              maxLength={MAX_SPLIT_SIZE}
              onChange={e => setContent(e.target.value)}
            />
          </div>
          {editable && (
            <span className='absolute c-font_1 text-12px pointer-events-none right-42px bottom-12px'>
              {content.length}/{MAX_SPLIT_SIZE}
            </span>
          )}
        </div>
        <div className='px-32 pb-24 flex items-center justify-end gap-12 absolute bottom-0 w-full left-0 bg-white'>
          {editable ? (
            <>
              <Button
                onClick={() => {
                  setEditable(false)
                  setContent(props.content)
                }}
              >
                取消
              </Button>
              <Button
                loading={loading}
                type='primary'
                onClick={async () => {
                  onSubmit()
                }}
              >
                保存
              </Button>
            </>
          ) : (
            <Button type='primary' onClick={() => setEditable(true)}>
              编辑
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default ParagraphEditorModal
